import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import SignaturePad from './SignaturePad';

// ── The W-9, signed here instead of chased ─────────────────────────────────
//
// A technician cannot be paid until QPS holds a signed W-9. Before this it was
// a scan in a text message, or a promise, and a first payment routinely sat a
// fortnight behind a form nobody could find.
//
// The wording is the IRS's and comes from the server, so the paragraph shown
// above the signature and the paragraph stored against it are the same one.
// A signature under text somebody paraphrased certifies nothing.

const CLASSIFICATIONS = [
  ['individual', 'Just me / sole proprietor', 'Most one-man businesses. Pick this if you file a Schedule C.'],
  ['llc', 'LLC', 'An LLC with more than one member, or one taxed as a corporation.'],
  ['s-corp', 'S corporation', ''],
  ['c-corp', 'C corporation', ''],
  ['partnership', 'Partnership', ''],
  ['trust-estate', 'Trust or estate', ''],
];

export default function W9Form() {
  const { vendorProfile, w9Wording, loadW9Wording, signW9, isSigningW9 } = useApp();
  const signed = vendorProfile?.w9 || {};
  const onFile = Boolean(signed.signedAt);

  const [open, setOpen] = useState(!onFile);
  const [f, setF] = useState({
    name: signed.name || vendorProfile?.businessName || '',
    businessName: signed.businessName || '',
    taxClassification: signed.taxClassification || 'individual',
    llcClassification: signed.llcClassification || '',
    address: signed.address || vendorProfile?.profile?.address || '',
    city: signed.city || vendorProfile?.profile?.city || '',
    state: signed.state || vendorProfile?.profile?.state || 'UT',
    zip: signed.zip || vendorProfile?.profile?.zip || '',
    taxIdType: signed.taxIdType || 'ssn',
    taxId: '',
    signedName: '',
    signatureImage: '',
  });
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  // Once, on open. loadW9Wording is recreated every render and caches itself,
  // so listing it here would fetch forever.
  useEffect(() => {
    loadW9Wording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (onFile && !open) {
    return (
      <div className="w9-card done">
        <div className="pay-head">
          <span className="pay-title">Your W-9</span>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setOpen(true)}>Sign a new one</button>
        </div>
        <div className="pay-row"><span>Signed</span><b>{new Date(signed.signedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</b></div>
        <div className="pay-row"><span>Name</span><b>{signed.name}</b></div>
        <div className="pay-row"><span>{signed.taxIdType === 'ein' ? 'EIN' : 'Social Security number'}</span><b>•••••{signed.taxIdLast4}</b></div>
        <p className="pay-note">QPS has what it needs to pay you and to file your 1099. Nothing to send in.</p>
      </div>
    );
  }

  return (
    <div className="w9-card">
      <div className="pay-head"><span className="pay-title">Your W-9</span></div>
      <p className="w9-intro">
        The tax form QPS needs before anybody can be paid. It takes two minutes
        and you sign it here with your finger. Nothing to print.
      </p>

      <Field label="Name on your tax return" id="w9Name"
        hint="Your own name, even if you work under a business name.">
        <input id="w9Name" type="text" value={f.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>

      <Field label="Business name, if different" id="w9Biz" hint="Leave blank if it is the same.">
        <input id="w9Biz" type="text" value={f.businessName} onChange={(e) => set({ businessName: e.target.value })} />
      </Field>

      <div className="qps-input-block">
        <label htmlFor="w9Class">How is your business taxed?</label>
        <select id="w9Class" value={f.taxClassification} onChange={(e) => set({ taxClassification: e.target.value })}>
          {CLASSIFICATIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <p className="qps-input-hint">
          {CLASSIFICATIONS.find(([v]) => v === f.taxClassification)?.[2] || 'Your accountant will know if you are not sure.'}
        </p>
      </div>

      {f.taxClassification === 'llc' && (
        <Field label="Your LLC is taxed as" id="w9Llc">
          <select id="w9Llc" value={f.llcClassification} onChange={(e) => set({ llcClassification: e.target.value })}>
            <option value="">Select…</option>
            <option value="S">An S corporation</option>
            <option value="C">A C corporation</option>
            <option value="P">A partnership</option>
          </select>
        </Field>
      )}

      <Field label="Address" id="w9Addr">
        <input id="w9Addr" type="text" value={f.address} onChange={(e) => set({ address: e.target.value })} autoComplete="address-line1" />
      </Field>
      <div className="w9-row3">
        <Field label="City" id="w9City">
          <input id="w9City" type="text" value={f.city} onChange={(e) => set({ city: e.target.value })} autoComplete="address-level2" />
        </Field>
        <Field label="State" id="w9State">
          <input id="w9State" type="text" maxLength={2} value={f.state}
            onChange={(e) => set({ state: e.target.value.toUpperCase() })} autoComplete="address-level1" />
        </Field>
        <Field label="ZIP" id="w9Zip">
          <input id="w9Zip" type="text" inputMode="numeric" value={f.zip}
            onChange={(e) => set({ zip: e.target.value })} autoComplete="postal-code" />
        </Field>
      </div>

      <Field label="Do you use a Social Security number or an EIN?" id="w9TinType">
        <select id="w9TinType" value={f.taxIdType} onChange={(e) => set({ taxIdType: e.target.value })}>
          <option value="ssn">Social Security number</option>
          <option value="ein">EIN (business tax number)</option>
        </select>
      </Field>

      <Field label={f.taxIdType === 'ein' ? 'Your EIN' : 'Your Social Security number'} id="w9Tin"
        hint="Scrambled before it is stored. QPS sees the last four digits.">
        <input id="w9Tin" type="text" inputMode="numeric" autoComplete="off" value={f.taxId}
          onChange={(e) => set({ taxId: e.target.value })}
          placeholder={f.taxIdType === 'ein' ? '12-3456789' : '123-45-6789'} />
      </Field>

      {/* The IRS's own words, from the server, so what is shown and what is
          stored against the signature can never drift apart. */}
      {w9Wording?.certification && (
        <div className="w9-cert">
          <div className="w9-cert-head">What you are signing</div>
          <pre className="w9-cert-body">{w9Wording.certification}</pre>
        </div>
      )}

      <div className="qps-input-block">
        <label htmlFor="w9Signed">Type your full name</label>
        <input id="w9Signed" type="text" value={f.signedName}
          onChange={(e) => set({ signedName: e.target.value })} placeholder="Mateo Ruiz" />
      </div>

      <SignaturePad onChange={(img) => set({ signatureImage: img })} disabled={isSigningW9} />

      <button type="button" className="pay-save" disabled={isSigningW9}
        onClick={() => signW9(f).then((ok) => ok && setOpen(false))}>
        {isSigningW9 ? 'Signing…' : 'Sign my W-9'}
      </button>
      {onFile && <button type="button" className="btn btn-ghost btn-lg" onClick={() => setOpen(false)}>Cancel</button>}
    </div>
  );
}

function Field({ label, id, hint, children }) {
  return (
    <div className="qps-input-block">
      <label htmlFor={id}>{label}</label>
      {hint && <p className="qps-input-hint">{hint}</p>}
      {children}
    </div>
  );
}
